import _ from 'lodash';

/** GraphQL/Mongoose requires a non-empty `source` string. */
const DEFAULT_ARTICLE_SOURCE = 'News';

/** When RSS/HTML has no explicit hero image, match Mercury `useImage` fallback (first img in content). */
const DEFAULT_ARTICLE_IMAGE =
  (typeof process !== 'undefined' &&
    process.env &&
    process.env.REACT_APP_DEFAULT_ARTICLE_IMAGE_URL &&
    String(process.env.REACT_APP_DEFAULT_ARTICLE_IMAGE_URL).trim()) ||
  'https://placehold.co/800x450/png?text=News';

const fallbackContent = (link) =>
  `<p><a href="${_.escape(link || '')}">Read full article</a></p>`;

function textFromHtml(html) {
  if (html == null || typeof html !== 'string') return '';
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * GraphQL/API requires a non-empty `content` string. Apollo may omit `undefined`
 * keys; RSS/Mercury can yield missing, null, "", or tag-only HTML.
 * Call this before every createArticle(record) mutation.
 */
export function ensureArticleRecordContent(record) {
  if (!record || typeof record !== 'object') return record;
  const link = record.link || '';
  let content = record.content;

  if (content == null) {
    return { ...record, content: fallbackContent(link) };
  }

  const str = String(content);
  if (str.trim().length === 0) {
    return { ...record, content: fallbackContent(link) };
  }

  const plain = textFromHtml(str);
  if (plain.length === 0 && !/<img\b/i.test(str)) {
    return { ...record, content: fallbackContent(link) };
  }

  return { ...record, content: str };
}

/**
 * Ensures `source` is a non-empty string (Apollo omits `undefined` keys in JSON).
 * Call before every createArticle(record) mutation, or use prepareCreateArticleRecord.
 */
export function ensureArticleRecordSource(record) {
  if (!record || typeof record !== 'object') return record;
  const raw = record.source;
  if (raw != null && String(raw).trim() !== '') {
    return { ...record, source: String(raw).trim() };
  }
  return { ...record, source: DEFAULT_ARTICLE_SOURCE };
}

/**
 * Resolves absolute image URL from first <img src> in HTML (same idea as mercury.js imageFromEncodedContent / firstImg).
 */
export function firstImageFromContent(html, link) {
  if (!html || typeof html !== 'string' || !link) return undefined;
  const m = html.match(/<img[^>]+src=["']([^"']+)["']/i);
  if (!m) return undefined;
  const src = m[1].trim();
  if (!src) return undefined;
  try {
    return new URL(src, link).href;
  } catch (_) {
    return src;
  }
}

/** Mongoose requires `image`; mirrors mercury.useImage fallbacks (RSS image, then first img in content, then default). */
export function ensureArticleRecordImage(record) {
  if (!record || typeof record !== 'object') return record;
  const raw = record.image;
  if (raw != null && String(raw).trim() !== '') {
    const s = String(raw).trim();
    try {
      return { ...record, image: new URL(s, record.link || undefined).href };
    } catch (_) {
      return { ...record, image: s };
    }
  }
  const fromContent = firstImageFromContent(record.content, record.link);
  if (fromContent) {
    return { ...record, image: fromContent };
  }
  return { ...record, image: DEFAULT_ARTICLE_IMAGE };
}

/** Normalizes `content`, `source`, and `image` for createArticle — use this at every mutation site. */
export function prepareCreateArticleRecord(record) {
  return ensureArticleRecordImage(ensureArticleRecordSource(ensureArticleRecordContent(record)));
}
